import Seo from "../components/Seo";
import { MarketingHeader, CTASection, FAQBlock } from "../components/MarketingShell";
import LegalFooter from "../components/LegalFooter";
import { Link } from "react-router-dom";

const COMPARISON = [
  { feature: "Preserves slang & idioms", ours: true, generic: false },
  { feature: "Keeps honorifics (bhai, sir, ji, aap)", ours: true, generic: false },
  { feature: "Handles Hinglish input", ours: true, generic: "sometimes" },
  { feature: "Editable subtitles against video", ours: true, generic: false },
  { feature: "Burn-in subtitled MP4 export", ours: true, generic: false },
  { feature: "21 Indian + global languages any-to-any", ours: true, generic: "partial" },
  { feature: "SRT + VTT downloads", ours: true, generic: true },
  { feature: "5 GB uploads", ours: true, generic: "usually 25 MB" },
  { feature: "Personality-preserving translation prompt", ours: true, generic: false },
];

const WORKFLOW = [
  { step: "Upload", body: "Drag your video or audio into the browser. MP4, MOV, WEBM, MP3, WAV — up to 5 GB." },
  { step: "Auto-transcribe", body: "Whisper detects timestamps and writes the transcript in the source language you pick." },
  { step: "Auto-translate", body: "GPT-5.4 rewrites each line into the target language with tone, humour and sarcasm preserved." },
  { step: "Edit if needed", body: "Live editor lets you click a segment, pause the video on it, retype the text, save." },
  { step: "Export", body: "Download SRT, VTT, or a burned-in MP4 you can post anywhere." },
];

const USE_CASES = [
  { title: "Automatic YouTube captions in another language", body: "Upload a Hindi video, get an English SRT that you paste into YouTube Studio → Subtitles → Add Language. YouTube's own auto-caption doesn't cross the language barrier — ours does." },
  { title: "Instagram Reels & TikTok subtitles", body: "Reels perform better with burnt-in text because 80% of viewers watch on mute. Render the subtitled MP4 in LinguaSub, upload it directly." },
  { title: "Corporate training localised", body: "Turn a Hindi onboarding video into English, Chinese, or French for global teams — batch through by re-running with different target languages." },
  { title: "Documentary & film festival subs", body: "Feature-length Indian films can be transcribed and translated in one pass, then hand-polished in the editor before festival submission." },
  { title: "Podcast to text", body: "Even without video, automatic subtitle translation gives you a translated transcript you can turn into blog posts, newsletters, or show notes." },
];

const FAQ = [
  { q: "What does 'automatic subtitle translator' actually mean?", a: "You upload media in one language. Software transcribes it into text with timestamps, translates each line into your target language, and outputs a subtitle file (SRT/VTT) or a video with the subtitles burned into the frame. No manual typing." },
  { q: "How does LinguaSub differ from Google Translate on YouTube?", a: "YouTube's built-in captions are language-locked — they show the original language only. Google Translate on top of that is literal, robotic, and misses tone. LinguaSub is a purpose-built tool: it uses a subtitle-specific translation prompt, gives you an editor, and exports finished files ready to publish." },
  { q: "Do I need special software installed?", a: "No. Everything runs in the browser. Chrome, Safari, Firefox, Edge, Brave — all work. Mobile browsers work too but a laptop is easier for editing." },
  { q: "How accurate is automatic subtitle translation?", a: "For clean audio in major languages: 90–95% accurate at the transcription step, 85–92% accurate at the translation step. LinguaSub's editor is designed for that last 5–15% — you pause the video on any imperfect segment and fix it in-place." },
  { q: "Can I automate this for many videos?", a: "The web UI is one-at-a-time today. A REST API for batch processing is on the Studio and Agency roadmap." },
  { q: "Which languages does LinguaSub cover?", a: "21 total, any-to-any: Hindi, Bhojpuri, Magahi, Awadhi, Urdu, Tamil, Telugu, Bengali, Malayalam, Gujarati, Punjabi, English, Chinese, French, German, Russian, Japanese, Marathi, Spanish, Korean, Italian." },
];

export default function AutomaticSubtitleTranslatorPage() {
  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <Seo path="/automatic-subtitle-translator" title="Automatic subtitle translator" description="Automatic subtitle translator for YouTube, Reels, TikTok, film festivals and corporate video. Whisper transcription + GPT-5.4 translation + editor + burn-in MP4 export. 21 languages." />
      <MarketingHeader />

      <section className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">Automatic subtitle translator</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-6 max-w-4xl">
            Automatic subtitle translation that <span className="text-blue-500">sounds human</span>.
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl leading-relaxed mb-8">
            Upload video, audio, or paste text. LinguaSub transcribes, translates with a subtitle-tuned GPT-5.4 model, and outputs an SRT, VTT, or subtitled MP4 ready to publish. No timeline software required.
          </p>
          <Link to="/" className="h-12 px-6 bg-white text-zinc-950 hover:bg-zinc-200 font-semibold text-sm inline-flex items-center rounded-none">Start free →</Link>
        </div>
      </section>

      <section className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">Workflow</div>
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-10 text-white">
            Five steps. No editor open. No terminal.
          </h2>
          <ol className="space-y-6">
            {WORKFLOW.map((w, i) => (
              <li key={w.step} className="grid grid-cols-[auto_1fr] gap-6 items-start">
                <div className="font-heading text-3xl font-black text-blue-500/70 tabular-nums">{String(i + 1).padStart(2, "0")}</div>
                <div>
                  <div className="text-lg font-bold text-white mb-1">{w.step}</div>
                  <p className="text-sm text-zinc-400 leading-relaxed">{w.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-zinc-800 bg-zinc-900/40">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-10 text-white">Use cases.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {USE_CASES.map((u) => (
              <div key={u.title} className="border border-zinc-800 bg-zinc-950 p-5">
                <h3 className="text-sm font-bold text-white mb-2">{u.title}</h3>
                <p className="text-xs text-zinc-400 leading-relaxed">{u.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-8 text-white">
            LinguaSub vs generic auto-translate.
          </h2>
          <div className="border border-zinc-800">
            <div className="grid grid-cols-[1fr_auto_auto] text-[10px] font-mono uppercase tracking-widest border-b border-zinc-800 bg-zinc-900/60">
              <div className="p-4 text-zinc-500">Capability</div>
              <div className="p-4 text-blue-400">LinguaSub</div>
              <div className="p-4 text-zinc-500 pr-6">Generic tools</div>
            </div>
            {COMPARISON.map((c) => (
              <div key={c.feature} className="grid grid-cols-[1fr_auto_auto] text-sm border-b border-zinc-800 last:border-b-0">
                <div className="p-4 text-zinc-300">{c.feature}</div>
                <div className="p-4 text-emerald-400 text-center min-w-[100px]">{c.ours === true ? "✓" : c.ours}</div>
                <div className="p-4 pr-6 text-zinc-500 text-center min-w-[120px]">{c.generic === true ? "✓" : c.generic === false ? "—" : c.generic}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQBlock items={FAQ} />
      <CTASection />
      <div className="max-w-6xl mx-auto px-4 md:px-8"><LegalFooter /></div>
    </div>
  );
}
